import axios from "axios";
import environments from "../environment";
import { ApiResultErrorException } from "../utils/constants";
import { proxyStorage } from "../utils/proxyStorage";
import { trackAdminEvent, trackAdminHttpError, adminTelemetryEndpoint } from "./AdminTelemetryService";

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);

function isBrowserLocalHost() {
	if (typeof window === "undefined") return false;
	return LOCAL_HOSTS.has(window.location.hostname);
}

function normalizeLocalBaseUrl(url?: string | null) {
	if (!url) return "";
	if (!isBrowserLocalHost()) return url;
	return url.replace("host.docker.internal", "localhost");
}

function resolveBaseUrl() {
	if (isBrowserLocalHost()) {
		return normalizeLocalBaseUrl(environments.localApiRoot) || environments.apiRoot || "";
	}
	return environments.apiRoot || environments.localApiRoot || "";
}

function resolveFakeNowFromUrl() {
	if (typeof window === "undefined") return "";
	try {
		const params = new URLSearchParams(window.location.search || "");
		return String(params.get("fakeNow") || "").trim();
	} catch (_error) {
		return "";
	}
}

const http = axios.create({
	headers: {},
	withCredentials: true, // Send httpOnly cookies automatically
});

http.interceptors.request.use(async (config) => {
	config.baseURL = resolveBaseUrl();

	// Backward-compatible: still send Authorization header if token exists in localStorage
	// This will be removed once all clients are migrated to cookie-based auth
	let accessToken = proxyStorage()?.getItem("token");
	let headers: any = config.headers;
	if (headers && !headers?.["Authorization"] && accessToken) {
		headers["Authorization"] = `Bearer ${accessToken}`;
	}
	const fakeNow = resolveFakeNowFromUrl();
	if (headers && fakeNow) {
		headers["X-Whalelo-Fake-Now"] = fakeNow;
	}

	config.params = {
		...config.params,
		_t: Date.now(),
	};

	return config;
});

http.interceptors.response.use(
	async (resp) => {
		if (resp.data && resp.data.isError) {
			void trackAdminEvent({
				eventType: "api_error",
				level: "warn",
				action: "api_soft_error",
				message: resp.data?.errorMessage || "isError",
				requestPath: String(resp.config?.url || ""),
			});
			throw `${ApiResultErrorException}, message: ${resp.data?.errorMessage || "none"}`;
		}
		return resp;
	},
	async (error) => {
		const originalRequest = error.config;
		const telemetryUrl = String(originalRequest?.url || "");
		if (telemetryUrl && !telemetryUrl.includes(adminTelemetryEndpoint)) {
			void trackAdminHttpError(error);
		}
		return Promise.reject(error);
	},
);

export default http;
